// Reposición (Inventario → Operaciones → Reposición, /inventario/reposicion):
// mínimos DINÁMICOS calculados de la demanda real (con candado manual), cobertura en
// días con semáforo, y sugerencias accionables: transferir del excedente de otra
// bodega (1 clic, ejecuta la transferencia) o generar la ORDEN DE COMPRA al proveedor
// habitual (consolidada). Tab de OCs (aprobar/anular/imprimir vía export) y Parámetros.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Ban, CheckCircle2, Loader2, Lock, PackagePlus, RefreshCw, ShoppingCart, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { Campo, fieldCls, invalidateInventario, invRpc, Location, locationsList, Modal, money, num, toNum } from './shared';
import { LOCALE, hoyISO } from '@/lib/utils/regional';

interface Excedente { location_id: string; bodega: string; disponible: number; clase?: string | null }
interface Sugerencia {
  location_id: string; bodega: string; producto_id: string; codigo: string; producto: string;
  unidades: number; stock_min: number; stock_max: number; manual: boolean;
  demanda_diaria: number; cobertura_dias: number | null; sugerido: number;
  transito_entrante: number; transito_atrasado: number; transito_eta: string | null; transito_codigo: string | null;
  clase?: string | null; clase_xyz?: string | null; es_lento?: boolean; prioridad?: number;
  proveedor_id: string | null; proveedor: string | null; ultimo_costo: number | null; ultima_compra: string | null;
  excedentes: Excedente[];
}
interface Accionable { producto_id: string; codigo: string; producto: string; marca: string | null; clase: string; dias_cobertura: number | null; stock: number; capital: number; es_regulado?: boolean; es_esencial?: boolean; es_estacional?: boolean; es_nuevo?: boolean; accion: string; destino_id: string | null; destino: string | null; destino_clase: string | null }
export const claseBadge = (c?: string | null) => {
  if (!c) return null;
  const cls = c === 'A' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : c === 'B' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
    : c === 'C' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    : 'bg-muted text-muted-foreground';
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${cls}`}>{c}</span>;
};
interface RepoConfig { ventana_dias: number; lead_time_default: number; dias_seguridad: number; cobertura_dias: number; recalculado_at: string | null }
interface ProvLead { partner_id: string; proveedor: string; lead_calc: number | null; n_recepciones: number; lead_override: number | null }
interface OcRow { oc_id: string; codigo: string; fecha: string; estado: string; proveedor: string; bodega: string; total: number; n_lineas: number; pedido: number; recibido: number; observaciones: string | null }

const ESTADOS_OC: Record<string, string> = { borrador: 'Borrador', aprobada: 'Aprobada', recibida_parcial: 'Recibida parcial', recibida: 'Recibida', anulada: 'Anulada' };

function Semaforo({ dias }: { dias: number | null }) {
  if (dias == null) return <span className="text-xs text-muted-foreground">sin demanda</span>;
  const cls = dias <= 3 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : dias <= 7 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  return <span className={`rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums ${cls}`}>{num(dias, 1)} días</span>;
}

export function Reposicion() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'sugerencias' | 'ordenes' | 'lentos' | 'parametros'>('sugerencias');
  const [local, setLocal] = useState('');
  const [transferir, setTransferir] = useState<{ s: Sugerencia; e: Excedente; cantidad: string } | null>(null);

  const { data: locales = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: config } = useQuery<RepoConfig>({ queryKey: ['inv-repo-config'], queryFn: () => invRpc('inv_repo_config_get'), staleTime: 0 });

  const recalcular = useMutation({
    mutationFn: () => invRpc<{ filas_actualizadas: number }>('inv_reposicion_recalcular'),
    onSuccess: (r) => { toast.success(`Mínimos recalculados (${r.filas_actualizadas} filas)`); invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-repo-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // recalcula solo al abrir si el dato tiene más de 24 h (no hay cron en el servidor)
  const stale = config && (!config.recalculado_at || Date.now() - new Date(config.recalculado_at).getTime() > 24 * 3600 * 1000);
  if (stale && !recalcular.isPending && !recalcular.isSuccess && !recalcular.isError) recalcular.mutate();

  const { data: sugerencias = [], isFetching } = useQuery<Sugerencia[]>({
    queryKey: ['inv-reposicion', local],
    queryFn: () => invRpc('inv_reposicion_listar', { _location: local || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const getVal = useCallback((s: Sugerencia, key: string): string | number | null => {
    switch (key) {
      case 'bodega': return s.bodega;
      case 'codigo': return s.codigo;
      case 'producto': return s.producto;
      case 'unidades': return Number(s.unidades);
      case 'min': return Number(s.stock_min);
      case 'cobertura': return s.cobertura_dias == null ? null : Number(s.cobertura_dias);
      case 'sugerido': return Number(s.sugerido);
      case 'proveedor': return s.proveedor;
      default: return null;
    }
  }, []);
  const sort = useSort(sugerencias, getVal);
  const filas = sort.sorted;

  // Selección para la OC consolidada
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const clave = (s: Sugerencia) => `${s.location_id}|${s.producto_id}`;
  const seleccionadas = filas.filter((s) => sel[clave(s)] && s.proveedor_id);
  const porProveedor = useMemo(() => {
    const m = new Map<string, Sugerencia[]>();
    for (const s of seleccionadas) { const k = s.proveedor_id!; m.set(k, [...(m.get(k) ?? []), s]); }
    return m;
  }, [seleccionadas]);

  const crearOcs = useMutation({
    mutationFn: async () => {
      const codigos: string[] = [];
      for (const [prov, items] of porProveedor) {
        // una OC por proveedor×bodega
        const porBodega = new Map<string, Sugerencia[]>();
        for (const s of items) porBodega.set(s.location_id, [...(porBodega.get(s.location_id) ?? []), s]);
        for (const [loc, ls] of porBodega) {
          const r = await invRpc<{ codigo: string }>('inv_oc_crear', {
            payload: {
              proveedor_partner_id: prov, location_id: loc,
              observaciones: 'Generada desde Reposición',
              lineas: ls.map((s) => ({ producto_id: s.producto_id, cantidad: Number(s.sugerido), costo_est: Number(s.ultimo_costo) || 0 })),
            },
          });
          codigos.push(r.codigo);
        }
      }
      return codigos;
    },
    onSuccess: (codigos) => { toast.success(`Orden(es) de compra en borrador: ${codigos.join(', ')}`); setSel({}); setTab('ordenes'); void qc.invalidateQueries({ queryKey: ['inv-ocs'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const ejecutarTransfer = useMutation({
    mutationFn: (t: { s: Sugerencia; e: Excedente; cantidad: number }) =>
      invRpc('inv_transferencia_confirmar', {
        payload: {
          location_origen_id: t.e.location_id, location_destino_id: t.s.location_id,
          fecha: hoyISO(),
          observaciones: `Reposición: ${t.s.codigo} a ${t.s.bodega}`,
          lineas: [{ producto_id: t.s.producto_id, cantidad: t.cantidad }],
        },
      }),
    onSuccess: () => { toast.success('Transferencia ejecutada'); setTransferir(null); invalidateInventario(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const candado = useMutation({
    mutationFn: (s: Sugerencia) => invRpc('inv_stock_minmax_set', {
      _location: s.location_id, _producto: s.producto_id,
      _min: Number(s.stock_min), _max: Number(s.stock_max), _manual: !s.manual,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inv-reposicion'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headers = ['N°', 'Bodega', 'Código', 'Producto', 'Stock', 'Mínimo', 'Máximo', 'Demanda/día', 'Cobertura (días)', 'En tránsito', 'Sugerido', 'Proveedor habitual', 'Últ. costo'];
  const filasExport: Celda[][] = filas.map((s, i) => [i + 1, s.bodega, s.codigo, s.producto, Number(s.unidades), Number(s.stock_min), Number(s.stock_max), Number(s.demanda_diaria), s.cobertura_dias, Number(s.transito_entrante || 0), Number(s.sugerido), s.proveedor, s.ultimo_costo] as Celda[]);

  const tabBtn = (t: typeof tab, label: string) => (
    <button className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`} onClick={() => setTab(t)}>{label}</button>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><PackagePlus className="w-5 h-5" /> Reposición</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            Mínimos recalculados: {config?.recalculado_at ? new Date(config.recalculado_at).toLocaleString(LOCALE) : 'nunca'}
          </span>
          <Button variant="outline" size="sm" isLoading={recalcular.isPending} onClick={() => recalcular.mutate()}>
            <RefreshCw className="w-4 h-4 mr-1" /> Recalcular ahora
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b pb-2">{tabBtn('sugerencias', 'Sugerencias')}{tabBtn('ordenes', 'Órdenes de compra')}{tabBtn('lentos', 'Lentos (dead-stock)')}{tabBtn('parametros', 'Parámetros')}</div>

      {tab === 'sugerencias' && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Campo label="Bodega">
              <select className={`${fieldCls} w-64`} value={local} onChange={(e) => setLocal(e.target.value)}>
                <option value="">— Todas —</option>{locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select>
            </Campo>
            <div className="ml-auto flex items-center gap-2">
              <Button disabled={!seleccionadas.length} isLoading={crearOcs.isPending} onClick={() => crearOcs.mutate()}>
                <ShoppingCart className="w-4 h-4 mr-1" /> Generar OC ({seleccionadas.length})
              </Button>
              <ExportarBotones nombre="reposicion_sugerida" titulo="Sugerencias de reposición"
                headers={headers} filas={filasExport} colsDerecha={[0, 4, 5, 6, 7, 8, 9, 11]}
                fmtPDF={(v, col) => (col === 11 && v != null && v !== '' ? money(Number(v)) : String(v ?? ''))}
                disabled={!filas.length} />
            </div>
          </div>

          <Card><CardContent className="overflow-x-auto pt-4">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2"><input type="checkbox" checked={filas.length > 0 && filas.every((s) => !s.proveedor_id || sel[clave(s)])}
                  onChange={(e) => { const c = e.target.checked; setSel(Object.fromEntries(filas.filter((s) => s.proveedor_id).map((s) => [clave(s), c]))); }} /></th>
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="bodega" sort={sort}>Bodega</SortTh>
                <SortTh col="codigo" sort={sort}>Código</SortTh>
                <SortTh col="producto" sort={sort}>Producto</SortTh>
                <th className="py-2 pr-3" title="Clase ABC del local (prioridad de reposición)">Clase</th>
                <SortTh col="unidades" sort={sort} align="right">Stock</SortTh>
                <SortTh col="min" sort={sort} align="right">Mín / Máx</SortTh>
                <SortTh col="cobertura" sort={sort} align="right">Cobertura</SortTh>
                <th className="py-2 pr-3 text-right text-xs font-medium text-muted-foreground">En tránsito</th>
                <SortTh col="sugerido" sort={sort} align="right">Sugerido</SortTh>
                <SortTh col="proveedor" sort={sort}>Reponer desde</SortTh>
                <th className="py-2 pr-3"></th>
              </tr></thead>
              <tbody>
                {filas.length === 0 && <tr><td colSpan={12} className="py-10 text-center text-muted-foreground">
                  {isFetching ? 'Consultando…' : 'Nada por reponer — todo sobre el mínimo 🎉'}</td></tr>}
                {filas.map((s, i) => (
                  <tr key={clave(s)} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-1.5 pr-2">{s.proveedor_id && <input type="checkbox" checked={!!sel[clave(s)]} onChange={(e) => setSel((m) => ({ ...m, [clave(s)]: e.target.checked }))} />}</td>
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3">{s.bodega}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{s.codigo}</td>
                    <td className="py-1.5 pr-3 max-w-[16rem] truncate" title={s.producto}>{s.producto}</td>
                    <td className="py-1.5 pr-3">{claseBadge(s.clase)}{s.es_lento && <span className="ml-1" title="Lento">🐌</span>}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(s.unidades, 0)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-xs">
                      {num(s.stock_min, 0)} / {num(s.stock_max, 0)}
                      <button className="ml-1 align-middle text-muted-foreground hover:text-foreground" title={s.manual ? 'Mínimo MANUAL (candado): el motor no lo recalcula — clic para volver a dinámico' : 'Mínimo dinámico — clic para fijarlo manual (candado)'}
                        onClick={() => candado.mutate(s)}>
                        {s.manual ? <Lock className="inline w-3.5 h-3.5 text-amber-600" /> : <Unlock className="inline w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="py-1.5 pr-3 text-right"><Semaforo dias={s.cobertura_dias} /></td>
                    <td className="py-1.5 pr-3 text-right text-xs">
                      {Number(s.transito_entrante) > 0 ? (
                        <span className="text-sky-700 dark:text-sky-300" title={`${s.transito_codigo ?? ''} · llega ~${s.transito_eta ?? '?'} — ya restado del sugerido`}>
                          🚚 +{num(s.transito_entrante, 0)}
                        </span>
                      ) : Number(s.transito_atrasado) > 0 ? (
                        <span className="text-red-600 dark:text-red-400" title="Tránsito ATRASADO — no se descuenta del sugerido; revísalo en Transferencias → Tránsito">
                          ⚠ +{num(s.transito_atrasado, 0)}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-semibold"
                        title={Number(s.transito_entrante) > 0 ? `máx ${num(s.stock_max, 0)} − stock ${num(s.unidades, 0)} − tránsito ${num(s.transito_entrante, 0)}` : undefined}>{num(s.sugerido, 0)}</td>
                    <td className="py-1.5 pr-3 text-xs">
                      {s.excedentes.length > 0
                        ? <span className="text-sky-700 dark:text-sky-300">{s.excedentes[0].bodega} tiene {num(s.excedentes[0].disponible, 0)} de sobra</span>
                        : s.proveedor
                          ? <span>{s.proveedor}{s.ultimo_costo != null ? ` · ${money(s.ultimo_costo)}` : ''}</span>
                          : <span className="text-muted-foreground">sin proveedor conocido</span>}
                    </td>
                    <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                      {s.excedentes.length > 0 && (
                        <button className="rounded p-1 text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20" title={`Transferir desde ${s.excedentes[0].bodega}`}
                          onClick={() => setTransferir({ s, e: s.excedentes[0], cantidad: String(Math.min(Number(s.sugerido), Number(s.excedentes[0].disponible))) })}>
                          <ArrowLeftRight className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
          <p className="text-xs text-muted-foreground">
            Semáforo: 🔴 ≤3 días de cobertura · 🟡 ≤7 · 🟢 más. Marca filas y "Generar OC" crea una orden
            en borrador por proveedor (con su último costo). El botón azul transfiere del excedente de otra bodega.
          </p>
        </>
      )}

      {tab === 'ordenes' && <OrdenesCompra />}
      {tab === 'lentos' && <LentosAccionables locales={locales} />}
      {tab === 'parametros' && <Parametros config={config} />}

      {transferir && (
        <Modal title={`Transferir ${transferir.s.codigo}`} onClose={() => setTransferir(null)}>
          <div className="space-y-3 text-sm">
            <p><span className="font-medium">{transferir.s.producto}</span></p>
            <p>Desde <span className="font-medium">{transferir.e.bodega}</span> (excedente {num(transferir.e.disponible, 0)}) hacia <span className="font-medium">{transferir.s.bodega}</span> (stock {num(transferir.s.unidades, 0)}, mínimo {num(transferir.s.stock_min, 0)}).</p>
            <Campo label="Cantidad a transferir">
              <input type="number" min="1" className={fieldCls} value={transferir.cantidad}
                onChange={(e) => setTransferir((t) => t && { ...t, cantidad: e.target.value })} />
            </Campo>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTransferir(null)}>Cancelar</Button>
              <Button isLoading={ejecutarTransfer.isPending}
                disabled={toNum(transferir.cantidad) <= 0 || toNum(transferir.cantidad) > Number(transferir.e.disponible)}
                onClick={() => ejecutarTransfer.mutate({ s: transferir.s, e: transferir.e, cantidad: toNum(transferir.cantidad) })}>
                <ArrowLeftRight className="w-4 h-4 mr-1" /> Ejecutar transferencia
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LentosAccionables({ locales }: { locales: Location[] }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [loc, setLoc] = useState('');
  useEffect(() => { if (!loc && locales.length) setLoc(locales[0].id); }, [locales, loc]);
  const { data: rows = [], isFetching } = useQuery<Accionable[]>({
    queryKey: ['inv-lentos-acc', loc], enabled: !!loc,
    queryFn: () => invRpc('inv_lentos_accionables', { _location: loc }), staleTime: 0,
  });
  const origen = locales.find((l) => l.id === loc);
  const transferir = useMutation({
    mutationFn: (r: Accionable) => invRpc('inv_transferencia_confirmar', {
      payload: {
        location_origen_id: loc, location_destino_id: r.destino_id,
        fecha: hoyISO(),
        observaciones: `Dead-stock: ${r.codigo} → ${r.destino}`,
        lineas: [{ producto_id: r.producto_id, cantidad: Number(r.stock) }],
      },
    }),
    onSuccess: () => { toast.success('Transferencia ejecutada'); invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-lentos-acc'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const capital = rows.reduce((a, r) => a + Number(r.capital || 0), 0);
  const nTransf = rows.filter((r) => r.accion === 'transferir').length;
  const nRevisar = rows.filter((r) => r.accion === 'revisar').length;
  const nLiquidar = rows.filter((r) => r.accion === 'liquidar').length;
  return (
    <>
      {dlg.ui}
      <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Lento movimiento — acciones ({rows.length})</CardTitle>
          <select className={`${fieldCls} !w-56`} value={loc} onChange={(e) => setLoc(e.target.value)}>
            {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
        </div>
        <p className="text-xs text-muted-foreground">Clase C/D con sobre-stock. Se sugiere <b>transferir</b> (1 clic) al local donde el producto es A/B (recupera precio completo), <b>liquidar</b> si no rota en ningún local, o <b>revisar</b> a mano cuando es regulado, esencial, de temporada o nuevo (baja rotación esperada).</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <div className="mb-3 flex gap-4 text-sm">
          <span className="rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5">Capital inmovilizado: <b className="text-amber-600">{money(capital)}</b></span>
          <span className="rounded-md bg-sky-50 dark:bg-sky-900/20 px-3 py-1.5">Transferibles: <b className="text-sky-600">{nTransf}</b> · A revisar: <b>{nRevisar}</b> · A liquidar: <b>{nLiquidar}</b></span>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Producto</th><th className="py-2 pr-3">Clase</th>
            <th className="py-2 pr-3 text-right">Stock</th><th className="py-2 pr-3 text-right">Cobertura (d)</th><th className="py-2 pr-3 text-right">Capital</th><th className="py-2 pr-3">Acción sugerida</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{isFetching ? 'Consultando…' : 'Sin lentos en esta bodega 🎉'}</td></tr>}
            {rows.slice(0, 300).map((r, i) => (
              <tr key={r.producto_id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-3">{r.producto} {r.marca && <span className="text-xs text-muted-foreground">· {r.marca}</span>}
                  {r.es_regulado && <span className="ml-1 rounded bg-rose-100 px-1 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" title="Regulado / controlado — no liquidar">Rx</span>}
                  {r.es_esencial && <span className="ml-1 rounded bg-indigo-100 px-1 py-0.5 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" title="Esencial — no descontinuar">Esencial</span>}
                  {r.es_estacional && <span className="ml-1 rounded bg-orange-100 px-1 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" title="De temporada — su baja rotación fuera de temporada es esperada">Temporada</span>}
                  {r.es_nuevo && <span className="ml-1 rounded bg-teal-100 px-1 py-0.5 text-[10px] font-semibold text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" title="Nuevo — en período de gracia, sin historia de ventas">Nuevo</span>}</td>
                <td className="py-1.5 pr-3">{claseBadge(r.clase)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{num(r.stock, 0)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.dias_cobertura == null ? '—' : r.dias_cobertura >= 999999 ? '∞' : num(r.dias_cobertura, 0)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums font-semibold">{money(r.capital)}</td>
                <td className="py-1.5 pr-3">
                  {r.accion === 'transferir' && r.destino_id
                    ? <Button size="sm" variant="outline" className="h-7"
        isLoading={transferir.isPending && transferir.variables?.producto_id === r.producto_id}
                        onClick={async () => {
                          if (await dlg.confirmar(`¿Ejecutar la transferencia de ${num(r.stock, 0)} u. de "${r.producto}"?`, {
                            detalle: `De ${origen?.nombre ?? 'esta bodega'} → ${r.destino} (clase ${r.destino_clase}).`,
                          })) transferir.mutate(r);
                        }}>
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Transferir → {r.destino} ({r.destino_clase})
                      </Button>
                    : r.accion === 'revisar'
                      ? <span className="inline-flex items-center gap-1 text-muted-foreground" title="Regulado/esencial/temporada/nuevo — decidir manualmente">Revisar</span>
                      : <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">Liquidar / promocionar</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      </Card>
    </>
  );
}

function OrdenesCompra() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [estado, setEstado] = useState('');
  const { data: ocs = [], isFetching } = useQuery<OcRow[]>({
    queryKey: ['inv-ocs', estado],
    queryFn: () => invRpc('inv_oc_listar', { _estado: estado || null }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const accion = useMutation({
    mutationFn: ({ id, a, motivo }: { id: string; a: string; motivo?: string }) =>
      invRpc<{ estado: string }>('inv_oc_estado', { _oc: id, _accion: a, _motivo: motivo ?? null }),
    onSuccess: (r) => { toast.success(`Orden ${ESTADOS_OC[r.estado] ?? r.estado}`); void qc.invalidateQueries({ queryKey: ['inv-ocs'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <>
      {dlg.ui}
      <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-3">
        Órdenes de compra {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
        <select className={`${fieldCls} h-8 ml-auto w-48 text-xs`} value={estado} onChange={(e) => setEstado(e.target.value)}>
          <option value="">— Todos los estados —</option>
          {Object.entries(ESTADOS_OC).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Fecha</th>
            <th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3">Bodega</th>
            <th className="py-2 pr-3 text-right">Líneas</th><th className="py-2 pr-3 text-right">Pedido</th>
            <th className="py-2 pr-3 text-right">Recibido</th><th className="py-2 pr-3 text-right">Total est.</th>
            <th className="py-2 pr-3">Estado</th><th className="py-2 pr-3"></th>
          </tr></thead>
          <tbody>
            {ocs.length === 0 && <tr><td colSpan={12} className="py-10 text-center text-muted-foreground">Sin órdenes de compra</td></tr>}
            {ocs.map((o, i) => (
              <tr key={o.oc_id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-3 font-mono text-xs">{o.codigo}</td>
                <td className="py-1.5 pr-3">{o.fecha}</td>
                <td className="py-1.5 pr-3 max-w-[14rem] truncate">{o.proveedor}</td>
                <td className="py-1.5 pr-3">{o.bodega}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{o.n_lineas}</td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(o.pedido, 0)}</td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(o.recibido, 0)}</td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(o.total)}</td>
                <td className="py-1.5 pr-3 text-xs">{ESTADOS_OC[o.estado] ?? o.estado}</td>
                <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                  {o.estado === 'borrador' && (
                    <button className="rounded p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20" title="Aprobar"
                      disabled={accion.isPending} onClick={() => accion.mutate({ id: o.oc_id, a: 'aprobar' })}>
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                  {(o.estado === 'borrador' || o.estado === 'aprobada') && (
                    <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Anular"
                      disabled={accion.isPending}
                      onClick={async () => {
                        const v = await dlg.pedir(`Anular ${o.codigo}`, [{ key: 'motivo', label: 'Motivo', minimo: 3 }]);
                        if (v) accion.mutate({ id: o.oc_id, a: 'anular', motivo: v.motivo.trim() });
                      }}>
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-muted-foreground">La recepción de mercadería (Inventario → Operaciones → Recepción) puede cargarse "desde OC": acumula lo recibido y cierra la orden.</p>
      </CardContent>
      </Card>
    </>
  );
}

function Parametros({ config }: { config: RepoConfig | undefined }) {
  const qc = useQueryClient();
  const [f, setF] = useState<RepoConfig | null>(null);
  const c = f ?? config ?? { ventana_dias: 90, lead_time_default: 7, dias_seguridad: 3, cobertura_dias: 30, recalculado_at: null };
  const { data: provs = [] } = useQuery<ProvLead[]>({ queryKey: ['inv-repo-provs'], queryFn: () => invRpc('inv_repo_proveedores'), staleTime: 0 });
  const guardar = useMutation({
    mutationFn: () => invRpc('inv_repo_config_set', { _data: { ventana_dias: c.ventana_dias, lead_time_default: c.lead_time_default, dias_seguridad: c.dias_seguridad, cobertura_dias: c.cobertura_dias } }),
    onSuccess: () => { toast.success('Parámetros guardados — recalcula para aplicarlos'); void qc.invalidateQueries({ queryKey: ['inv-repo-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const setLead = useMutation({
    mutationFn: ({ partner, lead }: { partner: string; lead: number | null }) => invRpc('inv_repo_proveedor_set', { _partner: partner, _lead: lead }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inv-repo-provs'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const campo = (label: string, key: keyof RepoConfig, ayuda: string) => (
    <Campo label={label}>
      <input type="number" min="0" className={fieldCls} value={String(c[key] ?? '')} title={ayuda}
        onChange={(e) => setF({ ...c, [key]: toNum(e.target.value) })} />
      <span className="text-[11px] text-muted-foreground">{ayuda}</span>
    </Campo>
  );
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Parámetros del motor</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {campo('Ventana de demanda (días)', 'ventana_dias', 'Cuántos días de historia de ventas/consumo se promedian (default 90).')}
          {campo('Lead time por defecto (días)', 'lead_time_default', 'Días que tarda un proveedor en entregar cuando no hay historial (default 7).')}
          {campo('Días de seguridad', 'dias_seguridad', 'Colchón extra sobre el lead time (default 3).')}
          {campo('Cobertura objetivo (días)', 'cobertura_dias', 'Días de venta que cubre el stock máximo al reponer (default 30).')}
          <p className="text-xs text-muted-foreground rounded bg-muted/50 px-2 py-1.5">
            mínimo = venta diaria × (lead time + seguridad) · máximo = mínimo + venta diaria × cobertura.
            Las filas con candado 🔒 no se recalculan.
          </p>
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar parámetros</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Lead time por proveedor</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3 text-right">Calculado</th>
              <th className="py-2 pr-3 text-right">Recepciones</th><th className="py-2 pr-3 text-right">Override</th>
            </tr></thead>
            <tbody>
              {provs.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Aún no hay recepciones confirmadas de proveedores</td></tr>}
              {provs.map((p) => (
                <tr key={p.partner_id} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 max-w-[14rem] truncate">{p.proveedor}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{p.lead_calc != null ? `${p.lead_calc} días` : '—'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{p.n_recepciones}</td>
                  <td className="py-1.5 pr-3 text-right">
                    <input type="number" min="1" max="90" placeholder="auto" className={`${fieldCls} h-8 w-20 ml-auto text-right`}
                      defaultValue={p.lead_override ?? ''}
                      onBlur={(e) => { const v = e.target.value === '' ? null : Math.max(1, Math.min(90, toNum(e.target.value))); if (v !== (p.lead_override ?? null)) setLead.mutate({ partner: p.partner_id, lead: v }); }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">El calculado es la mediana de días entre recepciones (últimos 180 días). El override manda sobre el calculado; vacío = automático.</p>
        </CardContent>
      </Card>
    </div>
  );
}
